import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import addressApi from "api/landlord/address";
import { Show } from "shared/components";
import {
  LabelPosition,
  Switch,
  SwitchSize
} from "shared/components/forms/Switch";
import { TreeRow } from "shared/components/SyndicationChannels/TreeRow";
import { UnitChannelsPopover } from "shared/components/SyndicationChannels/UnitChannelsPopover";
import { Unit } from "types/landlord/address";
import { getErrorMessage } from "utils/error";
import {
  invalidateNeedsLockboxCounts,
  invalidateUnitsLists,
  patchUnitInCaches
} from "shared/utils/units-cache";
import { useUnitAvailabilityPermission } from "shared/hooks/use-unit-availability-permission";
import {
  PERMISSION_DENIED_SWITCH_CLASS_NAME,
  PermissionDeniedTooltip
} from "shared/components/PermissionDeniedTooltip";
import { UnitCardField } from "../../visibilityConstants";

const stop = (event: React.MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

export const UnitCardToggles = ({
  unit,
  shows
}: {
  unit: Unit;
  shows: (field: string) => boolean;
}) => {
  const queryClient = useQueryClient();
  const magicLinkId = unit.magic_link?.id;
  const { canChangeUnitAvailability, unitAvailabilityDeniedTooltip } =
    useUnitAvailabilityPermission();
  const switchClassName = canChangeUnitAvailability
    ? undefined
    : PERMISSION_DENIED_SWITCH_CLASS_NAME;

  const { mutate } = useMutation({
    mutationKey: ["UNIT_MAGIC_LINK", unit.id, "UPDATE"],
    mutationFn: (payload: { active?: boolean; listed?: boolean }) =>
      magicLinkId
        ? addressApi.updateMagicLink(magicLinkId, payload)
        : Promise.resolve(null),
    onSuccess: (data, payload) => {
      if (!data) return;

      const needsLockbox: unknown = data.unit?.needs_lockbox;
      patchUnitInCaches(queryClient, unit.id, {
        ...payload,
        ...(typeof needsLockbox === "boolean"
          ? { needs_lockbox: needsLockbox }
          : {})
      });
      invalidateNeedsLockboxCounts(queryClient);
      if (payload.listed === false) invalidateUnitsLists(queryClient);
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });

  const showsAllow = shows(UnitCardField.AllowApplications);
  const showsList = shows(UnitCardField.ListUnit);

  return (
    <Show if={showsAllow || showsList}>
      <div onClick={stop} role="presentation" className="space-y-4">
        <Show if={showsAllow}>
          <PermissionDeniedTooltip tooltip={unitAvailabilityDeniedTooltip}>
            <Switch
              label="Allow applications"
              size={SwitchSize.SM}
              position={LabelPosition.Right}
              wrapperClassName="gap-2"
              checked={Boolean(unit.magic_link?.active)}
              disabled={!canChangeUnitAvailability}
              switchClassName={switchClassName}
              onChange={() => mutate({ active: !unit.magic_link?.active })}
            />
          </PermissionDeniedTooltip>
        </Show>

        <Show if={showsList}>
          <div className="pl-11">
            <TreeRow isLast spacing="card">
              <div className="flex items-center gap-2">
                <PermissionDeniedTooltip
                  tooltip={unitAvailabilityDeniedTooltip}
                >
                  <Switch
                    label="List unit"
                    size={SwitchSize.SM}
                    position={LabelPosition.Right}
                    wrapperClassName="gap-2"
                    checked={Boolean(unit.listed)}
                    disabled={!canChangeUnitAvailability}
                    switchClassName={switchClassName}
                    onChange={() => mutate({ listed: !unit.listed })}
                  />
                </PermissionDeniedTooltip>
                <UnitChannelsPopover unit={unit} />
              </div>
            </TreeRow>
          </div>
        </Show>
      </div>
    </Show>
  );
};
